import { Fragment, useMemo } from 'react'
import { Check, ChevronUp, ChevronDown } from 'lucide-react'
import useBracketStore from '@/stores/useBracketStore'

/**
 * "Set who advances" picker for one pool. The order written to
 * poolTiebreaks[pool_id] IS the pool standings (authoritative) — positions
 * 1..k in order, any unplaced team falls in below by record.
 *
 * Two UIs share that contract (Phase 2, owner 2026-05-29):
 *  - Player: a single ranked list of every team, pre-filled from their record,
 *    reordered with up/down arrows. The top `slots` sit above a cutoff line and
 *    advance to the Finals. Reordering #1 is one tap, not a full reselect.
 *  - Admin official results: the deliberate "tap the teams that advanced, in
 *    finish order" list (readiness requires an explicit selection). Unchanged.
 *
 * Props: pool — one element of poolStructure.pools.
 */
export default function AdvancerPicker({ pool }) {
  const teams = useBracketStore(s => s.teams)
  const setPoolTiebreak = useBracketStore(s => s.setPoolTiebreak)
  const isEditingOfficial = useBracketStore(s => s.isEditingOfficial)
  const storedOrder = useBracketStore(s => s.poolTiebreaks?.[pool.pool_id]) || []

  const teamById = useMemo(() => new Map(teams.map(t => [t.id, t])), [teams])
  const teamName = (id) => teamById.get(id)?.players?.map(p => p.name).join(' / ') || 'TBD'

  const slots = Math.min(4, pool.teams.length)
  const rankAll = slots >= pool.teams.length // ≤4-team pool: everyone advances

  // Teams best-record-first — the player's default order and the admin tap list.
  const byRecord = useMemo(
    () => [...pool.standings].sort((a, b) => b.wins - a.wins),
    [pool.standings],
  )

  const onChange = (order) => setPoolTiebreak(pool.pool_id, order)

  if (isEditingOfficial) {
    return (
      <OfficialTapList
        pool={pool} rows={byRecord} slots={slots}
        order={storedOrder} teamName={teamName} onChange={onChange}
      />
    )
  }

  return (
    <PlayerRankList
      pool={pool} byRecord={byRecord} slots={slots} rankAll={rankAll}
      storedOrder={storedOrder} teamName={teamName} onChange={onChange}
    />
  )
}

const SHELL = 'rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700/50 p-3 space-y-2'

/** Player flow: one ranked list of every team, reordered with arrows. */
function PlayerRankList({ pool, byRecord, slots, rankAll, storedOrder, teamName, onChange }) {
  const standingsById = useMemo(
    () => new Map(pool.standings.map(s => [s.bracket_team_id, s])),
    [pool.standings],
  )
  const recordIds = useMemo(() => byRecord.map(s => s.bracket_team_id), [byRecord])

  // Full ranked order of every team: stored order first (authoritative), then
  // any team not yet placed appended by record. Pure record order until the
  // player touches it, so the pre-fill matches what the Finals derive from.
  const fullOrder = useMemo(() => {
    const placed = storedOrder.filter(id => standingsById.has(id))
    const rest = recordIds.filter(id => !placed.includes(id))
    return [...placed, ...rest]
  }, [storedOrder, recordIds, standingsById])

  const move = (idx, dir) => {
    const j = idx + dir
    if (j < 0 || j >= fullOrder.length) return
    const next = [...fullOrder]
    ;[next[idx], next[j]] = [next[j], next[idx]]
    onChange(next)
  }

  return (
    <div className={SHELL}>
      <p className="text-sm font-semibold">
        {rankAll
          ? `Pool ${pool.pool_id} — set your finishing order`
          : `Pool ${pool.pool_id} — order who advances to the Finals`}
      </p>
      <ul className="space-y-1">
        {fullOrder.map((id, idx) => {
          const s = standingsById.get(id)
          const pos = idx + 1
          const advancing = pos <= slots
          const losses = Math.max((s?.matches_played ?? 0) - (s?.wins ?? 0), 0)
          return (
            <Fragment key={id}>
              <li
                className={
                  'flex items-center gap-2 px-2 py-1.5 rounded border bg-background '
                  + (advancing ? 'border-blue-400' : 'opacity-60')
                }
              >
                <span
                  className={
                    'h-6 w-6 shrink-0 inline-flex items-center justify-center rounded-full text-xs font-semibold '
                    + (advancing ? 'bg-blue-600 text-white' : 'border text-muted-foreground')
                  }
                >
                  {pos}
                </span>
                <span className="flex-1 text-sm truncate">{teamName(id)}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{s?.wins ?? 0}-{losses}</span>
                <span className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button" aria-label="Move up" disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    className="p-1 rounded hover:bg-muted/50 disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button" aria-label="Move down" disabled={idx === fullOrder.length - 1}
                    onClick={() => move(idx, 1)}
                    className="p-1 rounded hover:bg-muted/50 disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </span>
              </li>
              {!rankAll && pos === slots && (
                <li className="flex items-center gap-2 px-1 text-[10px] font-medium uppercase tracking-wide text-blue-700 dark:text-blue-300">
                  <span className="h-px flex-1 bg-blue-300" />
                  advances to Finals
                  <span className="h-px flex-1 bg-blue-300" />
                </li>
              )}
            </Fragment>
          )
        })}
      </ul>
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Check className="h-3 w-3 text-[var(--color-success)]" />
        {rankAll
          ? 'Pre-filled from your picks — use the arrows to set the order.'
          : 'Pre-filled from your picks — use the arrows to change who advances.'}
      </p>
    </div>
  )
}

/** Admin official-results flow: deliberate tap-to-build list (Phase 1 owner decision). */
function OfficialTapList({ pool, rows, slots, order, teamName, onChange }) {
  const rank = new Map(order.map((id, i) => [id, i + 1]))

  const toggle = (teamId) => {
    if (rank.has(teamId)) {
      onChange(order.filter(id => id !== teamId))
    } else if (order.length >= slots) {
      // At the cap (e.g. 4 of 5): swap in for the current last advancer rather
      // than stamping a confusing extra badge.
      onChange([...order.slice(0, slots - 1), teamId])
    } else {
      onChange([...order, teamId])
    }
  }

  const chosen = order.length
  const done = chosen >= slots

  return (
    <div className={SHELL}>
      <p className="text-sm font-semibold">
        Pool {pool.pool_id} — tap the {slots} teams that advanced, in finish order
      </p>
      <ul className="space-y-1">
        {rows.map(s => {
          const id = s.bracket_team_id
          const pos = rank.get(id)
          const losses = Math.max((s.matches_played ?? 0) - s.wins, 0)
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => toggle(id)}
                className={
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded border text-left transition-colors '
                  + (pos ? 'border-blue-400 bg-background' : 'bg-background hover:bg-muted/50')
                }
              >
                <span
                  className={
                    'h-6 w-6 shrink-0 inline-flex items-center justify-center rounded-full border text-xs font-semibold '
                    + (pos ? 'bg-blue-600 text-white border-blue-600' : 'border-dashed text-muted-foreground')
                  }
                >
                  {pos || ''}
                </span>
                <span className="flex-1 text-sm truncate">{teamName(id)}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{s.wins}-{losses}</span>
              </button>
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        {done ? (
          <>
            <Check className="h-3 w-3 text-[var(--color-success)]" />
            {slots} advancers set — save the bracket to apply.
          </>
        ) : (
          `Tap ${slots - chosen} more. Tap a numbered team to remove it.`
        )}
      </p>
    </div>
  )
}
